import React, { useState } from 'react';

interface University {
  id: number;
  name: string;
  ai_uni_level_specific_advice: string;
}

interface AIModelGuidanceProps {
  university: University;
  formData: Partial<University>;
  onInputChange: (field: keyof University, value: any) => void;
  onSave: () => Promise<void>;
  saving?: boolean;
}

const AIModelGuidance: React.FC<AIModelGuidanceProps> = ({
  university,
  formData,
  onInputChange,
  onSave,
  saving = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const adviceText = formData.ai_uni_level_specific_advice || university.ai_uni_level_specific_advice || '';

  return (
    <div className="mt-8 bg-white rounded-lg shadow-lg">
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">AI Model Guidance</h3>
        </div>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-semibold text-gray-800">University-Specific Advice</h4>
            <div className="flex items-center space-x-2">
              {!isEditing ? (
                <button 
                  onClick={() => setIsEditing(true)}
                  disabled={saving}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit advice"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              ) : (
                <>
                  <button 
                    onClick={async () => {
                      await onSave();
                      setIsEditing(false);
                    }}
                    disabled={saving}
                    className="p-2 text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Save changes"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => {
                      setIsEditing(false);
                      onInputChange('ai_uni_level_specific_advice', university.ai_uni_level_specific_advice || '');
                    }}
                    disabled={saving}
                    className="p-2 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Cancel changes"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="text-gray-700 leading-relaxed">
            {isEditing ? (
              <textarea
                value={adviceText}
                onChange={(e) => onInputChange('ai_uni_level_specific_advice', e.target.value)}
                disabled={saving}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-32 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Provide specific advice for AI models when working with this university's requirements..."
              />
            ) : (
              <>
                {adviceText && adviceText.trim() !== '' ? (
                  <p>{adviceText}</p>
                ) : (
                  <p className="italic text-gray-400">No university-specific advice configured yet.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIModelGuidance;